import {gql} from '@apollo/client';

import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BulkFetchBoxInfoForRowQuery} from 'notebook/structured-tables/row-updaters/__generated/BulkFetchBoxInfoForRowQuery';
import {BulkFetchPlateInfoForRowQuery} from 'notebook/structured-tables/row-updaters/__generated/BulkFetchPlateInfoForRowQuery';
import {
  BulkRefreshParentStorableCapacityQuery,
  BulkRefreshParentStorableCapacityQueryVariables,
} from 'notebook/structured-tables/row-updaters/__generated/BulkRefreshParentStorableCapacityQuery';
import {DirectLookupInfo, SampleLookupInfo} from 'notebook/structured-tables/StructuredTableConstants';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

export type ParentStorableCapacityInfo = ExcludeByTypename<
  ExcludeInaccessible<BulkRefreshParentStorableCapacityQuery['storables'][number]>,
  'ContainerNode'
>;

export function isParentStorableCapacityInfo(
  modelInfo:
    | ParentStorableCapacityInfo
    | InaccessibleInfoGraphQL
    | ExtractByTypename<BulkFetchPlateInfoForRowQuery['storables'][number], 'PlateNode'>
    | ExtractByTypename<BulkFetchBoxInfoForRowQuery['storables'][number], 'BoxNode'>
): modelInfo is ParentStorableCapacityInfo {
  return modelInfo.hasOwnProperty('occupiedCapacity');
}

export function isUnfinalizedParentStorableSample(sample: SampleLookupInfo): boolean {
  return Boolean((sample as DirectLookupInfo).isUnfinalizedParentStorable);
}

export const BULK_REFRESH_PARENT_STORABLE_CAPACITY_QUERY = gql`
  query BulkRefreshParentStorableCapacityQuery($parentStorableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $parentStorableIds) {
      ...ParentStorableCapacityGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ParentStorableCapacity').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function _createInaccessibleInfoNode(apiIdentifier: string): InaccessibleInfoGraphQL {
  return {
    __typename: 'InaccessibleInfoNode',
    invalidId: apiIdentifier,
    canRequestAccess: false,
  };
}

export async function fetchParentStorableCapacityModel(
  apiIdentifier: string
): Promise<ParentStorableCapacityInfo | InaccessibleInfoGraphQL> {
  const response = await doQuerySafe<
    BulkRefreshParentStorableCapacityQuery,
    BulkRefreshParentStorableCapacityQueryVariables
  >(
    {query: BULK_REFRESH_PARENT_STORABLE_CAPACITY_QUERY},
    {variables: {parentStorableIds: [apiIdentifier]}, fetchPolicy: 'network-only'}
  );
  // Containers aren't ever valid, so just handle as an inaccessible info node for downstream typesafety
  if (response?.storables[0]?.__typename === 'ContainerNode') {
    return _createInaccessibleInfoNode(apiIdentifier);
  }
  return response.storables[0];
}

export function bulkFetchParentStorableCapacityModel(
  apiIdentifiers: Array<string>
): Promise<Array<ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
  return doQuerySafe<BulkRefreshParentStorableCapacityQuery, BulkRefreshParentStorableCapacityQueryVariables>(
    {query: BULK_REFRESH_PARENT_STORABLE_CAPACITY_QUERY},
    {variables: {parentStorableIds: apiIdentifiers}, fetchPolicy: 'network-only'}
  ).then((response) =>
    response.storables.map((storable) =>
      storable.__typename === 'ContainerNode' ? _createInaccessibleInfoNode(storable.id) : storable
    )
  );
}

export function readParentStorableCapacityFromCache(
  apiIdentifier: string
): ParentStorableCapacityInfo | null {
  const data = readQuerySafe<
    BulkRefreshParentStorableCapacityQuery,
    BulkRefreshParentStorableCapacityQueryVariables
  >(
    {query: BULK_REFRESH_PARENT_STORABLE_CAPACITY_QUERY},
    {variables: {parentStorableIds: [apiIdentifier]}, fetchPolicy: 'cache-only'}
  );
  if (
    data == null ||
    !data.storables[0] ||
    isInaccessible(data.storables[0]) ||
    data.storables[0].__typename === 'ContainerNode'
  ) {
    return null;
  }
  return data.storables[0];
}
